import { tr, usePartnerTranslation } from "../../lib/language";
import React, { useMemo, useState } from "react";
import axios from "axios";
import { parseApiError } from "../../lib/api";
import { formatPartnerDate as format } from "../../lib/language";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { cn } from "../../lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_STYLES = {
  confirmed: "bg-[#D4F7DF] text-[#0F6B2B]",
  canceled_by_customer: "bg-[#FFE3A8] text-[#7A4E00]",
  canceled_by_supplier: "bg-[#FFBFBF] text-[#9B0000]",
  full_refund: "bg-[#D4CDFB] text-[#3326A3]",
  partial_refund: "bg-[#BDEFF4] text-[#0B5F6B]",
};

const ROLE_LABEL = { adult: "Adult", child: "Child", infant: "Infant" };

const formatActivityDate = (iso) => {
  if (!iso) return "—";
  try {
    return format(new Date(`${iso}T00:00:00`), "EEE, d MMM yyyy");
  } catch {
    return iso;
  }
};

const formatShortDate = (iso) => {
  if (!iso) return "—";
  try {
    return format(new Date(`${iso}T00:00:00`), "d MMM yyyy");
  } catch {
    return iso;
  }
};

const formatCreated = (iso) => {
  if (!iso) return "—";
  try {
    return format(new Date(iso), "d MMM yyyy 'at' h:mm a");
  } catch {
    return iso;
  }
};

const fmt = (n, cur = "$") =>
  `${cur} ${(Number(n) || 0).toFixed(2)}`;

const formatProductType = (booking) =>
  booking?.product_type || booking?.type || "Tour";

/* ---------------- Reusable label/value row (no icons) ---------------- */
const InfoRow = ({
  label,
  value,
  testid,
  valueClassName
}) => {
  usePartnerTranslation();
  return <div data-testid={testid} className="flex items-start justify-between gap-4 py-2">
    <dt className="text-sm text-[#6B7280]">{label}</dt>
    <dd className={cn("text-end text-sm font-medium text-[#0F1115]", valueClassName)}>
      {value}
    </dd>
  </div>;
};

const SectionTitle = ({
  children
}) => {
  usePartnerTranslation();
  return <h4 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#6B7280]">
    {children}
  </h4>;
};

/* ---------------- Customer identity mask + safe reply relay ---------------- */
const MaskedEmail = ({ maskedEmail, relayEmail, expiresAt }) => {
  usePartnerTranslation();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    if (!relayEmail) return;
    try {
      await navigator.clipboard.writeText(relayEmail);
      setCopied(true);
      toast.success(tr("Relay address copied"), { description: relayEmail });
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(tr("Couldn't copy — please try again"));
    }
  };
  return (
    <div data-testid="masked-email-row" className="py-2">
      <div className="flex items-center justify-between gap-4">
        <span className="text-sm text-[#6B7280]">{tr("Customer email")}</span>
        <div className="flex items-center gap-2">
          <span
            data-testid="masked-email-value"
            className="select-all font-mono text-sm text-[#0F1115]"
          >
            {maskedEmail || "—"}
          </span>
          {relayEmail && (
          <button
            type="button"
            data-testid="masked-email-copy"
            onClick={copy}
            aria-label={tr("Copy secure email relay")}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-[#E6E8EC] text-[#6B7280] transition-colors hover:border-[#0F1115] hover:text-[#0F1115]"
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-[#0F6B2B]" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </button>
          )}
        </div>
      </div>
      {relayEmail && (
        <div className="mt-2 flex items-center justify-between gap-4">
          <span className="text-sm text-[#6B7280]">{tr("Secure reply address")}</span>
          <span data-testid="customer-email-relay-value" className="select-all font-mono text-sm font-medium text-[#0F1115]">
            {relayEmail}
          </span>
        </div>
      )}
      <p
        data-testid="masked-email-expiry"
        className="mt-1 text-end text-[11px] text-[#9BA1AB]"
      >{tr("Relay expires ")}{formatShortDate(expiresAt)}
      </p>
    </div>
  );
};

/* ---------------- Price breakdown ---------------- */
const PriceBreakdown = ({ booking }) => {
  usePartnerTranslation();
  const adults = booking.adults || 0;
  const children = booking.children || 0;
  const infants = booking.infants || 0;
  const prices = booking.prices || {};
  const cur = booking.currency || "$";

  const isGroup = booking.pricing_basis === "per_group";
  const tiers = isGroup ? [] : [
    { key: "adult", label: tr("Adult"), count: adults, unit: prices.adult || 0 },
    { key: "child", label: tr("Child"), count: children, unit: prices.child || 0 },
    { key: "infant", label: tr("Infant"), count: infants, unit: prices.infant || 0 },
  ].filter((t) => t.count > 0);

  // Saved booking totals are authoritative; the adult field holds the entire
  // charge for per-group bookings and must never be multiplied by participants.
  const subtotal = Number(booking.retail_price ?? tiers.reduce((sum, t) => sum + t.count * t.unit, 0));
  const rate = booking.commission_rate ?? 0.15;
  const commission = Number(booking.commission_amount ?? Math.round((subtotal * rate + Number.EPSILON) * 100) / 100);
  const payout = Number(booking.partner_payout ?? Math.round((subtotal - commission + Number.EPSILON) * 100) / 100);

  return (
    <div className="rounded-xl border border-[#F0EFEF] bg-white">
      <dl className="divide-y divide-[#F0EFEF]">
        {isGroup && (
          <div data-testid="price-row-group" className="flex items-center justify-between px-4 py-3">
            <dt className="text-sm text-[#6B7280]">{tr("Group/vehicle price")}<span className="ms-2 text-[#9BA1AB]">{adults + children + infants}{tr(" Participants")}</span>
            </dt>
            <dd className="text-sm font-medium text-[#0F1115]">{fmt(subtotal, cur)}</dd>
          </div>
        )}
        {tiers.map((t) => (
          <div
            key={t.key}
            data-testid={`price-row-${t.key}`}
            className="flex items-center justify-between px-4 py-3"
          >
            <dt className="text-sm text-[#6B7280]">
              {t.label}
              <span className="ms-2 text-[#9BA1AB]">×{t.count}</span>
              <span className="ms-2 text-[#9BA1AB]">@ {fmt(t.unit, cur)}</span>
            </dt>
            <dd className="text-sm font-medium text-[#0F1115]">
              {fmt(t.count * t.unit, cur)}
            </dd>
          </div>
        ))}
        <div className="flex items-center justify-between px-4 py-3">
          <dt className="text-sm text-[#6B7280]">{tr("Subtotal")}</dt>
          <dd
            data-testid="price-subtotal"
            className="text-sm font-semibold text-[#0F1115]"
          >
            {fmt(subtotal, cur)}
          </dd>
        </div>
        <div className="flex items-center justify-between px-4 py-3">
          <dt className="text-sm text-[#6B7280]">{tr("Commission")}</dt>
          <dd
            data-testid="price-commission"
            className="text-sm font-semibold text-[#FF5757]"
          >
            − {fmt(commission, cur)}
          </dd>
        </div>
        <div className="flex items-center justify-between bg-[#FFFBFB] px-4 py-3">
          <dt className="text-sm font-bold text-[#0F1115]">{tr("Partner payout")}</dt>
          <dd
            data-testid="price-payout"
            className="text-base font-extrabold text-[#0F1115]"
          >
            {fmt(payout, cur)}
          </dd>
        </div>
      </dl>
    </div>
  );
};

/* ---------------- Tickets list ---------------- */
const TicketsList = ({ tickets, participantsOnly = false }) => {
  usePartnerTranslation();
  if (!tickets || tickets.length === 0) return null;
  return (
    <ul data-testid="tickets-list" className="divide-y divide-[#F0EFEF] rounded-xl border border-[#F0EFEF]">
      {tickets.map((t) => (
        <li
          key={t.number}
          data-testid={`ticket-${t.number}`}
          className="flex items-center justify-between px-4 py-3"
        >
          <span className="font-mono text-sm text-[#0F1115]">{t.number}</span>
          <span className="text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
            {participantsOnly ? tr("Participant") : ROLE_LABEL[t.role] || t.role}
          </span>
        </li>
      ))}
    </ul>
  );
};

/* ---------------- Cancellation flow ---------------- */
const CANCEL_STAGES = { FORM: "form", REVIEW: "review", DONE: "done" };

const CANCELLATION_REASON_GROUPS = [
  {
    get label() { return tr("Operational Reasons"); },
    reasons: [
      {
        code: "guide_driver_ill_emergency",
        get label() { return tr("Guide or driver is ill or had an emergency"); },
      },
      { code: "vehicle_breakdown", get label() { return tr("Vehicle breakdown or out of service"); } },
      {
        code: "activity_unavailable_booked_datetime",
        get label() { return tr("Activity unavailable on the booked date or time"); },
      },
      {
        code: "venue_unavailable_maintenance",
        get label() { return tr("Venue not available or closed for scheduled maintenance"); },
      },
      {
        code: "activity_no_longer_offered",
        get label() { return tr("Activity or option no longer offered"); },
      },
      {
        code: "public_holiday_local_event",
        get label() { return tr("Activity unavailable due to public holiday or local event"); },
      },
      {
        code: "activity_description_inaccurate",
        get label() { return tr("Activity description was inaccurate"); },
      },
      {
        code: "booked_language_unavailable",
        get label() { return tr("Activity not available in the booked language"); },
      },
    ],
  },
  {
    get label() { return tr("Booking Cannot Be Confirmed"); },
    reasons: [
      { code: "activity_fully_booked", get label() { return tr("Activity is fully booked"); } },
      {
        code: "last_minute_insufficient_time",
        get label() { return tr("Last-minute booking — insufficient time to confirm"); },
      },
      {
        code: "minimum_group_size_not_reached",
        get label() { return tr("Minimum group size not reached"); },
      },
      {
        code: "technical_booking_system_issue",
        get label() { return tr("Technical issue with the booking system"); },
      },
    ],
  },
  {
    get label() { return tr("Force Majeure"); },
    reasons: [
      { code: "terrorism", get label() { return tr("Act of terrorism"); } },
      { code: "severe_weather", get label() { return tr("Severe weather conditions"); } },
      { code: "power_internet_outage", get label() { return tr("Power or internet outage"); } },
      {
        code: "government_institutional_restrictions",
        get label() { return tr("Government or institutional restrictions"); },
      },
      { code: "natural_disaster", get label() { return tr("Natural disaster"); } },
      {
        code: "pandemic_disease_outbreak",
        get label() { return tr("Pandemic or disease outbreak"); },
      },
      { code: "political_unrest", get label() { return tr("Political unrest"); } },
      {
        code: "strike_industrial_action",
        get label() { return tr("Strike or industrial action"); },
      },
      { code: "war_armed_conflict", get label() { return tr("War or armed conflict"); } },
    ],
  },
  {
    get label() { return tr("Other"); },
    reasons: [
      {
        code: "customer_requested_cancellation",
        get label() { return tr("Customer requested cancellation"); },
      },
    ],
  },
];

const CANCELLATION_REASONS = CANCELLATION_REASON_GROUPS.flatMap(
  (group) => group.reasons,
);

const LEGACY_CANCELLATION_REASON_BY_CODE = {
  guide_driver_ill_emergency: "supplier_unavailable",
  vehicle_breakdown: "activity_not_operational",
  activity_unavailable_booked_datetime: "activity_not_operational",
  venue_unavailable_maintenance: "activity_not_operational",
  activity_no_longer_offered: "activity_not_operational",
  public_holiday_local_event: "activity_not_operational",
  activity_description_inaccurate: "other",
  booked_language_unavailable: "activity_not_operational",
  activity_fully_booked: "overbooked",
  last_minute_insufficient_time: "other",
  minimum_group_size_not_reached: "other",
  technical_booking_system_issue: "other",
  terrorism: "force_majeure",
  severe_weather: "weather_safety",
  power_internet_outage: "force_majeure",
  government_institutional_restrictions: "force_majeure",
  natural_disaster: "force_majeure",
  pandemic_disease_outbreak: "force_majeure",
  political_unrest: "force_majeure",
  strike_industrial_action: "force_majeure",
  war_armed_conflict: "force_majeure",
  customer_requested_cancellation: "other",
};

const CancellationFlow = ({
  booking,
  onBack,
  onCompleted,
}) => {
  usePartnerTranslation();
  const [stage, setStage] = useState(CANCEL_STAGES.FORM);
  const [reasons, setReasons] = useState(CANCELLATION_REASONS);
  const [reasonGroups, setReasonGroups] = useState(CANCELLATION_REASON_GROUPS);
  const [reasonCode, setReasonCode] = useState("");
  const [backendSupportsGroupedReasons, setBackendSupportsGroupedReasons] =
    useState(true);
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/bookings/meta/cancellation-reasons`,
        );
        if (!cancelled) {
          const hasGroupedReasons =
            Array.isArray(res.data.groups) && res.data.groups.length;
          const groups = hasGroupedReasons
            ? res.data.groups
            : CANCELLATION_REASON_GROUPS;
          const flatReasons = hasGroupedReasons
            ? groups.flatMap((group) => group.reasons || [])
            : Array.isArray(res.data.reasons) && res.data.reasons.length
              ? res.data.reasons
              : CANCELLATION_REASONS;

          setBackendSupportsGroupedReasons(Boolean(hasGroupedReasons));
          setReasonGroups(groups);
          setReasons(flatReasons);
        }
      } catch (err) {
        if (!cancelled) {
          setBackendSupportsGroupedReasons(true);
        }
        if (!cancelled && process.env.NODE_ENV !== "production") {
          console.warn("[cancel] load reasons failed:", err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const reasonLabel = useMemo(
    () =>
      reasonGroups
        .flatMap((group) => group.reasons || [])
        .find((r) => r.code === reasonCode)?.label ||
      reasons.find((r) => r.code === reasonCode)?.label ||
      "",
    [reasonGroups, reasons, reasonCode],
  );

  const reasonCodeForSubmit = backendSupportsGroupedReasons
    ? reasonCode
    : LEGACY_CANCELLATION_REASON_BY_CODE[reasonCode] || reasonCode;

  const submit = async () => {
    setSubmitting(true);
    setServerError("");
    try {
      const res = await axios.post(
        `${API}/bookings/${booking.reference}/cancel`,
        { reason: reasonCodeForSubmit, details: details.trim() || undefined },
      );
      setStage(CANCEL_STAGES.DONE);
      onCompleted?.(res.data.booking);
    } catch (err) {
      const { message } = parseApiError(err, tr("Couldn't process cancellation — please try again."));
      setServerError(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (stage === CANCEL_STAGES.FORM) {
    return (
      <div className="space-y-6" data-testid="cancellation-form">
        <div>
          <h3 className="text-lg font-extrabold tracking-tight text-[#0F1115]">{tr("Request cancellation")}</h3>
          <p className="mt-1 text-sm text-[#6B7280]">{tr("Pick the reason that best describes what happened. The customer will be notified immediately. Note that cancellations may incur a penalty of 10% of the booking value.")}</p>
        </div>

        <div className="space-y-2">
          <label
            htmlFor="cancel-reason"
            className="text-sm font-semibold text-[#0F1115]"
          >{tr("Cancellation reason")}</label>
          <Select value={reasonCode} onValueChange={setReasonCode}>
            <SelectTrigger
              id="cancel-reason"
              data-testid="cancel-reason-select"
              className="h-11 w-full rounded-lg border-[#D7DAE0] bg-white text-start"
            >
              <SelectValue placeholder={tr("Select a cancellation reason")} />
            </SelectTrigger>
            <SelectContent className="max-h-[min(420px,70vh)]">
              {reasonGroups.map((group) => (
                <SelectGroup key={group.label}>
                  <SelectLabel className="text-xs font-extrabold uppercase tracking-wide text-[#0F1115]">
                    {group.label}
                  </SelectLabel>
                  {(group.reasons || []).map((reason) => (
                    <SelectItem
                      key={reason.code}
                      value={reason.code}
                      data-testid={`cancel-reason-${reason.code}`}
                      className="ps-8"
                    >
                      {reason.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label
            htmlFor="cancel-details"
            className="text-sm font-semibold text-[#0F1115]"
          >{tr("Add a note for the customer (optional)")}</label>
          <textarea
            id="cancel-details"
            data-testid="cancel-details-input"
            rows={3}
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            placeholder={tr("e.g. the weather forecast turned unsafe for the scheduled slot.")}
            className="mt-2 w-full resize-none rounded-lg border border-[#D7DAE0] bg-white p-3 text-sm text-[#0F1115] placeholder:text-[#B6BAC2] outline-none transition-all focus:border-[#FF5757] focus:shadow-[0_0_0_4px_rgba(255,87,87,0.1)]"
          />
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-[#EFE4E4] pt-4">
          <button
            type="button"
            onClick={onBack}
            data-testid="cancel-form-back"
            className="text-sm font-semibold text-[#6B7280] hover:text-[#0F1115]"
          >{tr("← Back to booking")}</button>
          <button
            type="button"
            data-testid="cancel-form-next"
            disabled={!reasonCode}
            onClick={() => {
              if (!reasonLabel) {
                setServerError(tr("Please select a valid cancellation reason."));
                return;
              }
              setServerError("");
              setStage(CANCEL_STAGES.REVIEW);
            }}
            className="humrahe-btn-primary inline-flex h-11 items-center rounded-lg px-5 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50"
          >{tr("Continue to review")}</button>
        </div>
      </div>
    );
  }

  if (stage === CANCEL_STAGES.REVIEW) {
    return (
      <div className="space-y-6" data-testid="cancellation-review">
        <div>
          <h3 className="text-lg font-extrabold tracking-tight text-[#0F1115]">{tr("Please review before cancelling")}</h3>
          <p className="mt-1 text-sm text-[#6B7280]">{tr("This will cancel")}{" "}
            <span className="font-semibold text-[#0F1115]">
              {booking.reference}
            </span>{" "}{tr("and send a cancellation email to the customer and to you.")}</p>
        </div>

        <dl className="divide-y divide-[#F0EFEF] rounded-xl border border-[#F0EFEF] bg-white">
          <div className="flex items-start justify-between gap-4 px-4 py-3">
            <dt className="text-sm text-[#6B7280]">{tr("Product")}</dt>
            <dd className="text-end text-sm font-medium text-[#0F1115]">
              {booking.product_title}
            </dd>
          </div>
          <div className="flex items-start justify-between gap-4 px-4 py-3">
            <dt className="text-sm text-[#6B7280]">{tr("Activity")}</dt>
            <dd className="text-end text-sm font-medium text-[#0F1115]">
              {formatActivityDate(booking.activity_date)} · {booking.activity_time}
            </dd>
          </div>
          <div className="flex items-start justify-between gap-4 px-4 py-3">
            <dt className="text-sm text-[#6B7280]">{tr("Reason")}</dt>
            <dd
              data-testid="cancel-review-reason"
              className="text-end text-sm font-semibold text-[#0F1115]"
            >
              {reasonLabel || "—"}
            </dd>
          </div>
          {details.trim() && (
            <div className="flex items-start justify-between gap-4 px-4 py-3">
              <dt className="text-sm text-[#6B7280]">{tr("Note")}</dt>
              <dd className="max-w-[60%] whitespace-pre-wrap text-end text-sm text-[#0F1115]">
                {details.trim()}
              </dd>
            </div>
          )}
        </dl>

        <div className="rounded-lg border border-[#FFBFBF] bg-[#FFF5F5] p-4 text-sm text-[#9B0000]">{tr("Cancelling is irreversible. The customer will be notified immediately and may be eligible for a full refund per the booking terms.")}</div>

        {serverError && (
          <p
            data-testid="cancel-error"
            className="rounded-md bg-[#FFE9E9] px-3 py-2 text-sm text-[#9B0000]"
          >
            {serverError}
          </p>
        )}

        <div className="flex items-center justify-between gap-3 border-t border-[#EFE4E4] pt-4">
          <button
            type="button"
            onClick={() => setStage(CANCEL_STAGES.FORM)}
            data-testid="cancel-review-back"
            className="text-sm font-semibold text-[#6B7280] hover:text-[#0F1115]"
          >{tr("← Edit reason")}</button>
          <button
            type="button"
            disabled={submitting || !reasonCode || !reasonLabel}
            onClick={submit}
            data-testid="cancel-confirm-btn"
            className="inline-flex h-11 items-center rounded-lg bg-[#9B0000] px-5 text-sm font-semibold text-white transition-colors hover:bg-[#7a0000] disabled:opacity-60"
          >
            {submitting ? tr("Cancelling…") : tr("Confirm cancellation")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="cancellation-success"
      className="flex flex-col items-center space-y-4 py-8 text-center"
    >
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#D4F7DF] text-[#0F6B2B]">
        <Check className="h-6 w-6" />
      </div>
      <div>
        <h3 className="text-lg font-extrabold tracking-tight text-[#0F1115]">{tr("Booking cancelled")}</h3>
        <p className="mt-1 max-w-sm text-sm text-[#6B7280]">{tr("We've notified the customer and sent you a confirmation email. The booking now shows as ")}<span className="font-semibold">{tr("Canceled by Partner")}</span>.
        </p>
      </div>
    </div>
  );
};

/* ---------------- Main modal ---------------- */
const BookingDetailsModal = ({ booking, onClose, onBookingUpdate }) => {
  usePartnerTranslation();
  const [mode, setMode] = useState("view"); // "view" | "cancel"
  const [liveBooking, setLiveBooking] = useState(booking);
  const [now, setNow] = useState(Date.now);
  const startsAt = Date.parse(liveBooking?.activity_starts_at_utc);
  const canCancel = liveBooking?.status === "confirmed" && Number.isFinite(startsAt) && now < startsAt;

  React.useEffect(() => {
    if (!booking) return undefined;
    const refresh = () => setNow(Date.now());
    refresh();
    // Wake at the cutoff, including when a long-lived modal crosses the start time.
    const delay = Number.isFinite(startsAt) ? Math.min(Math.max(startsAt - Date.now(), 0), 2147483647) : 0;
    const timer = Number.isFinite(startsAt) && startsAt > Date.now() ? setTimeout(refresh, delay) : null;
    window.addEventListener("focus", refresh);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("focus", refresh);
    };
  }, [booking, startsAt, now]);

  React.useEffect(() => {
    if (liveBooking?.status === "confirmed" && !canCancel) setMode("view");
  }, [liveBooking, canCancel]);

  React.useEffect(() => {
    setLiveBooking(booking);
    setMode("view");
  }, [booking]);

  if (!liveBooking) return null;
  const open = !!booking;


  const handleCancelCompleted = (updated) => {
    setLiveBooking(updated);
    onBookingUpdate?.(updated);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="booking-details-modal"
        className="max-h-[92vh] max-w-[720px] overflow-y-auto rounded-2xl border-[#EFE4E4] p-0"
      >
        <div className="border-b border-[#EFE4E4] px-6 py-5 sm:px-8">
          <DialogHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <DialogTitle
                  data-testid="booking-details-reference"
                  className="font-mono text-lg font-extrabold tracking-tight text-[#0F1115] sm:text-xl"
                >
                  {liveBooking.reference}
                </DialogTitle>
                <DialogDescription className="mt-1 text-sm text-[#6B7280]">{tr("Booked ")}{formatCreated(liveBooking.created_at)}
                </DialogDescription>
              </div>
              <span
                data-testid={`booking-details-status-${liveBooking.status}`}
                className={cn(
                  "inline-flex h-7 items-center rounded-full px-3 text-xs font-semibold",
                  STATUS_STYLES[liveBooking.status] ||
                    "bg-[#F3EEEE] text-[#6B7280]",
                )}
              >
                {liveBooking.status_label}
              </span>
            </div>
          </DialogHeader>
        </div>

        <div className="px-6 py-6 sm:px-8">
          {mode === "cancel" && (canCancel || liveBooking.status !== "confirmed") ? (
            <CancellationFlow
              booking={liveBooking}
              onBack={() => setMode("view")}
              onCompleted={handleCancelCompleted}
            />
          ) : (
            <div className="space-y-8">
              {/* Product */}
              <section>
                <SectionTitle>{tr("Product")}</SectionTitle>
                <p
                  data-testid="detail-product-type"
                  className="mt-2 text-xs font-bold uppercase tracking-wide text-[#6B7280]"
                >
                  {formatProductType(liveBooking)}
                </p>
                <h3
                  data-testid="detail-product"
                  className="mt-1 text-lg font-extrabold tracking-tight text-[#0F1115]"
                >
                  {liveBooking.product_title}
                </h3>
                <p className="mt-1 text-sm text-[#6B7280]">{tr("Option ")}<span className="font-semibold text-[#0F1115]">{liveBooking.option_code}</span>
                </p>
              </section>

              {/* Activity */}
              <section>
                <SectionTitle>{tr("Activity details")}</SectionTitle>
                <dl className="mt-3 divide-y divide-[#F0EFEF] rounded-xl border border-[#F0EFEF] bg-white px-4">
                  <InfoRow
                    label={tr("Date")}
                    value={formatActivityDate(liveBooking.activity_date)}
                    testid="detail-activity-date"
                  />
                  <InfoRow
                    label={tr("Time")}
                    value={liveBooking.activity_time}
                    testid="detail-activity-time"
                  />
                  <InfoRow
                    label={tr("Language")}
                    value={liveBooking.language || "—"}
                    testid="detail-language"
                  />
                </dl>
              </section>

              {/* Travellers */}
              <section>
                <SectionTitle>{liveBooking.pricing_basis === "per_group" ? tr("Participants") : tr("Travellers")}</SectionTitle>
                <dl className="mt-3 divide-y divide-[#F0EFEF] rounded-xl border border-[#F0EFEF] bg-white px-4">
                  {liveBooking.pricing_basis === "per_group" ? (
                    <InfoRow
                      label={tr("Participants")}
                      value={(liveBooking.adults || 0) + (liveBooking.children || 0) + (liveBooking.infants || 0)}
                      testid="detail-participants"
                    />
                  ) : (<>
                  <InfoRow
                    label={tr("Adult")}
                    value={liveBooking.adults || 0}
                    testid="detail-adults"
                  />
                  <InfoRow
                    label={tr("Children")}
                    value={liveBooking.children || 0}
                    testid="detail-children"
                  />
                  <InfoRow
                    label={tr("Infants")}
                    value={liveBooking.infants || 0}
                    testid="detail-infants"
                  />
                  </>)}
                </dl>
              </section>

              {/* Customer */}
              <section>
                <SectionTitle>{tr("Customer")}</SectionTitle>
                <dl className="mt-3 rounded-xl border border-[#F0EFEF] bg-white px-4">
                  <InfoRow
                    label={tr("Name")}
                    value={liveBooking.customer?.name || "—"}
                    testid="detail-customer-name"
                  />
                  {liveBooking.customer?.phone && (
                    <InfoRow
                      label={tr("Phone")}
                      value={liveBooking.customer.phone}
                      testid="detail-customer-phone"
                    />
                  )}
                  {(liveBooking.customer?.masked_email || liveBooking.customer?.relay_email) && (
                    <div className="border-t border-[#F0EFEF]">
                      <MaskedEmail
                        maskedEmail={liveBooking.customer.masked_email}
                        relayEmail={liveBooking.customer.relay_email}
                        expiresAt={liveBooking.customer.contact_expires_at}
                      />
                    </div>
                  )}
                </dl>
              </section>

              {/* Tickets */}
              <section>
                <SectionTitle>{tr("Tickets")}</SectionTitle>
                <div className="mt-3">
                  <TicketsList tickets={liveBooking.tickets} participantsOnly={liveBooking.pricing_basis === "per_group"} />
                </div>
              </section>

              {/* Price */}
              <section>
                <SectionTitle>{tr("Price breakdown")}</SectionTitle>
                <div className="mt-3">
                  <PriceBreakdown booking={liveBooking} />
                </div>
              </section>

              {liveBooking.status === "canceled_by_supplier" && liveBooking.cancellation_reason_label && (
                <section
                  data-testid="cancellation-note"
                  className="rounded-lg border border-[#FFBFBF] bg-[#FFF5F5] p-4"
                >
                  <SectionTitle>{tr("Cancellation")}</SectionTitle>
                  <p className="mt-2 text-sm text-[#0F1115]">
                    <span className="font-semibold">{tr("Reason:")}</span>{" "}
                    {liveBooking.cancellation_reason_label}
                  </p>
                  {liveBooking.cancellation_details && (
                    <p className="mt-1 text-sm text-[#6B7280]">
                      {liveBooking.cancellation_details}
                    </p>
                  )}
                </section>
              )}
            </div>
          )}
        </div>

        {mode === "view" && (
          <div className="flex items-center justify-between gap-3 border-t border-[#EFE4E4] bg-white px-6 py-4 sm:px-8">
            <div>
              {canCancel && (
                <button
                  type="button"
                  onClick={() => setMode("cancel")}
                  data-testid="request-cancellation-btn"
                  className="inline-flex h-11 items-center rounded-lg border border-[#9B0000] px-5 text-sm font-semibold text-[#9B0000] transition-colors hover:bg-[#9B0000] hover:text-white"
                >{tr("Request cancellation")}</button>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              data-testid="booking-details-close"
              className="inline-flex h-11 items-center rounded-lg border border-[#E6E8EC] px-5 text-sm font-semibold text-[#0F1115] transition-colors hover:border-[#0F1115]"
            >{tr("Close")}</button>
          </div>
        )}

        {mode === "cancel" && (
          <div className="flex items-center justify-end gap-3 border-t border-[#EFE4E4] bg-white px-6 py-4 sm:px-8">
            <button
              type="button"
              onClick={onClose}
              data-testid="cancel-close"
              className="inline-flex h-11 items-center rounded-lg border border-[#E6E8EC] px-5 text-sm font-semibold text-[#0F1115] transition-colors hover:border-[#0F1115]"
            >{tr("Close")}</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookingDetailsModal;
